/**
 * The closest human categories of a text (DESIGN UD-33): for each human stratum of the reference taken on its own, how many
 * of the text's channel scores fall inside that stratum's range at this length. Pure: no DOM, no Node imports, no new model.
 * Everything comes from `compareToHuman` (the Channels view's comparison, UD-24), called with one stratum at a time.
 *
 * A stratum is listed only when at least two thirds of the text's scored channels have a measured pool in it (30 or more
 * human documents of about this length, CD-4); otherwise it is named as unmeasured. A listed stratum's score is the share
 * of its measured channels that fall within its range (the middle 90% of its documents); ties go to the smaller mean
 * distance from its medians, then to the id. It is a resemblance by channel ranges, never a probability or a verdict.
 */
import { type ChannelId, isShortTextChannel } from '../engine/channels/types.ts';
import {
	type HumanBand,
	type HumanComparison,
	type HumanReference,
	compareToHuman,
	defaultStrata,
} from './human-reference.ts';
import type { BinFigure } from './model-figures.ts';

/** A channel of the analysed text: its id and score (null where it did not score). */
export interface ReadingChannel {
	id: ChannelId;
	score: number | null;
}

type Measured = Extract<HumanComparison, { status: 'measured' }>;

/** One channel that makes the text resemble a category. */
export interface CategorySignal {
	channel: ChannelId;
	score: number;
	/** The category's pool for this channel: its middle 90%, its median, the documents behind it and the band of the score. */
	inCategory: {
		range: [number, number];
		median: number;
		n: number;
		band: HumanBand;
		distance: number;
	};
	/** All ten original-English categories pooled: the same, to show how general human writing differs. */
	inPooled: { range: [number, number]; n: number; band: HumanBand };
}

export interface CategoryFit {
	stratum: string;
	/** Human documents in the stratum of the reference (the whole stratum, all lengths). */
	documents: number;
	/** The text's scored channels, and how many of them have a measured pool in this stratum at this length. */
	scored: number;
	measured: number;
	within: number;
	outside: number;
	far: number;
	/** Mean of the measured channels' distances from the stratum's medians (smaller is closer). */
	meanDistance: number;
	/** The smallest and the largest pool behind a measured channel. */
	poolMin: number;
	poolMax: number;
	/**
	 * Up to three channels. 'distinguishing': inside this category's range and outside the pooled range of all ten
	 * original-English categories, nearest the category's median first. 'nearest': when no channel is both, the three
	 * channels nearest the category's median.
	 */
	signalsKind: 'distinguishing' | 'nearest';
	signals: CategorySignal[];
}

export interface UnmeasuredCategory {
	stratum: string;
	documents: number;
	scored: number;
	measured: number;
	/** The largest pool behind any scored channel at this length, measured or not (0 when none). */
	poolMax: number;
}

export interface ClosestCategories {
	/** The text's scored channels. */
	scored: number;
	/**
	 * The reference's channels that apply to this text: all of them, except a short-text channel (LX-04) that did not
	 * score, so the "few channels" note keeps its threshold at lengths where LX-04 never scores (REFIT-S).
	 */
	applicable: number;
	/** Listed categories, closest first. */
	listed: CategoryFit[];
	/** Categories with too few human documents of this length for two thirds of the channels. */
	unmeasured: UnmeasuredCategory[];
}

/** Two thirds of the scored channels must be measured for a category to be listed (integer test). */
export const listedEnough = (measured: number, scored: number): boolean =>
	scored > 0 && 3 * measured >= 2 * scored;

const isMeasured = (c: HumanComparison): c is Measured => c.status === 'measured';

const SIGNALS = 3;

export function closestCategories(
	ref: HumanReference,
	channels: readonly ReadingChannel[],
	words: number
): ClosestCategories {
	const scoredChannels = channels.filter(
		(c): c is { id: ChannelId; score: number } => c.score !== null && Number.isFinite(c.score)
	);
	const pooledSet = new Set(defaultStrata(ref));
	// the all-categories comparison of each channel, once
	const pooled = new Map(
		scoredChannels.map((c) => [c.id, compareToHuman(ref, c.id, c.score, words, pooledSet)] as const)
	);
	const listed: CategoryFit[] = [];
	const unmeasured: UnmeasuredCategory[] = [];
	for (const s of ref.strata) {
		const one = new Set([s.id]);
		const cmps = scoredChannels.map((c) => ({
			c,
			cmp: compareToHuman(ref, c.id, c.score, words, one),
		}));
		const measured = cmps.filter((x): x is { c: (typeof scoredChannels)[number]; cmp: Measured } =>
			isMeasured(x.cmp)
		);
		// the largest pool behind any scored channel, measured or not (an unmeasured comparison still carries its n)
		const poolMax = Math.max(
			0,
			...cmps.map((x) => (x.cmp.status === 'not-applicable' ? 0 : x.cmp.n))
		);
		if (!listedEnough(measured.length, scoredChannels.length)) {
			unmeasured.push({
				stratum: s.id,
				documents: s.n,
				scored: scoredChannels.length,
				measured: measured.length,
				poolMax,
			});
			continue;
		}
		let within = 0;
		let outside = 0;
		let far = 0;
		let dist = 0;
		for (const { cmp } of measured) {
			if (cmp.band === 'within') within++;
			else outside++;
			if (cmp.band === 'far') far++;
			dist += cmp.distance;
		}
		const signal = ({ c, cmp }: (typeof measured)[number]): CategorySignal | null => {
			const p = pooled.get(c.id);
			if (!p || !isMeasured(p)) return null;
			return {
				channel: c.id,
				score: c.score,
				inCategory: {
					range: cmp.range,
					median: cmp.median,
					n: cmp.n,
					band: cmp.band,
					distance: cmp.distance,
				},
				inPooled: { range: p.range, n: p.n, band: p.band },
			};
		};
		const byDistance = (a: CategorySignal, b: CategorySignal): number =>
			a.inCategory.distance - b.inCategory.distance || (a.channel < b.channel ? -1 : 1);
		const all = measured.map(signal).filter((x): x is CategorySignal => x !== null);
		const distinguishing = all
			.filter((x) => x.inCategory.band === 'within' && x.inPooled.band !== 'within')
			.sort(byDistance);
		const signalsKind = distinguishing.length > 0 ? 'distinguishing' : 'nearest';
		const signals = (
			signalsKind === 'distinguishing' ? distinguishing : [...all].sort(byDistance)
		).slice(0, SIGNALS);
		listed.push({
			stratum: s.id,
			documents: s.n,
			scored: scoredChannels.length,
			measured: measured.length,
			within,
			outside,
			far,
			meanDistance: dist / measured.length,
			poolMin: Math.min(...measured.map((x) => x.cmp.n)),
			poolMax,
			signalsKind,
			signals,
		});
	}
	listed.sort(
		(a, b) =>
			b.within / b.measured - a.within / a.measured ||
			a.meanDistance - b.meanDistance ||
			(a.stratum < b.stratum ? -1 : 1)
	);
	unmeasured.sort((a, b) => (a.stratum < b.stratum ? -1 : 1));
	const scoredIds = new Set(scoredChannels.map((c) => c.id));
	const applicable = ref.channels.filter(
		(id) => !isShortTextChannel(id as ChannelId) || scoredIds.has(id as ChannelId)
	).length;
	return { scored: scoredChannels.length, applicable, listed, unmeasured };
}

/** The claims-table bin a text of `proseWords` words falls in, or null (below the first bin, or a model without bins). */
export function binFor(bins: readonly BinFigure[], proseWords: number): BinFigure | null {
	return bins.find((b) => proseWords >= b.from && proseWords < b.to) ?? null;
}
